import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { collection, doc, onSnapshot, query, where, getDoc } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import StudentReflectionModal from './StudentReflectionModal';
import { Squad } from '../../../types';

const StudentReflectionListener: React.FC = () => {
  const { userData } = useAuth();
  const [active, setActive] = useState<{
    squadId: string;
    sessionId: string;
    levelId: string;
    curriculumId?: string;
    version?: number;
  } | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const studentId = userData?.id || '';

  const shouldListen = useMemo(() => userData?.role === 'student' && !!studentId, [userData?.role, studentId]);

  useEffect(() => {
    if (!shouldListen) return;

    const squadsRef = collection(db, 'squads');
    const q = query(squadsRef, where('studentIds', 'array-contains', studentId));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      let found: { squadId: string; sessionId: string; levelId: string; curriculumId?: string; version?: number } | null = null;

      snapshot.forEach((s) => {
        const data = { id: s.id, ...s.data() } as unknown as Squad & any;
        const ars = (data as any).activeReflectionSession;
        if (!ars) return;
        const levelId = String(ars.levelId || '');
        const sessionId = String(ars.sessionId || '');
        if (!levelId || !sessionId) return;
        found = { squadId: data.id, sessionId, levelId, curriculumId: (ars.curriculumId || 'music')?.toLowerCase(), version: ars.version };
      });

      if (!found) {
        setIsOpen(false);
        setActive(null);
        return;
      }

      // Before opening, check completion to avoid prompting finished students
      try {
        const f = found as { squadId: string; sessionId: string; levelId: string; curriculumId?: string; version?: number };
        const ref = doc(db, 'users', studentId, 'reflectionGame', 'state');
        const snap = await getDoc(ref);
        let isDone = false;
        if (snap.exists()) {
          const state = snap.data() as any;
          const bySC = state?.completedLevelsBySquadCurriculum?.[f.squadId]?.[f.curriculumId || 'music'];
          const scopedDone = bySC && bySC[f.levelId];
          const legacyDone = state?.completedLevels?.[f.levelId];
          isDone = !!(scopedDone || legacyDone);
        }
        if (isDone) {
          setIsOpen(false);
          setActive(f);
        } else {
          setActive(f);
          setIsOpen(true);
        }
      } catch (e) {
        // On error, default to opening to avoid blocking student responses
        setActive(found as any);
        setIsOpen(true);
      }
    }, (error) => {
      console.error('StudentReflectionListener squad query error', error);
    });

    return () => unsubscribe();
  }, [shouldListen, studentId]);

  if (!isOpen || !active || !studentId) return null;

  return (
    <StudentReflectionModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      studentId={studentId}
      squadId={active.squadId}
      sessionId={active.sessionId}
      levelId={active.levelId}
    />
  );
};

export default StudentReflectionListener;
